import { CircleCheckBig, CircleX, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';
import type { PENDING_SUBMISSION_STATUS } from '../constants/instituteSubmission.constants';
import type { SubmissionStatus } from '../types/instituteSubmission.types';

type ReviewedSubmissionStatus = Exclude<SubmissionStatus, typeof PENDING_SUBMISSION_STATUS>;

interface ReviewOutcomeDisplay {
  title: string;
  icon: LucideIcon;
  surfaceClass: string;
  accentClass: string;
}

const REVIEW_OUTCOME_DISPLAY: Record<ReviewedSubmissionStatus, ReviewOutcomeDisplay> = {
  APPROVED: {
    title: 'This submission was approved',
    icon: CircleCheckBig,
    surfaceClass: 'border-success-foreground/30 bg-success/60',
    accentClass: 'text-success-foreground',
  },
  REJECTED: {
    title: 'This submission was rejected',
    icon: CircleX,
    surfaceClass: 'border-danger-foreground/30 bg-danger/60',
    accentClass: 'text-danger-foreground',
  },
};

interface InstituteSubmissionReviewSummaryProps {
  status: ReviewedSubmissionStatus;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewerComment: string | null;
}

export function InstituteSubmissionReviewSummary({
  status,
  reviewedByName,
  reviewedAt,
  reviewerComment,
}: InstituteSubmissionReviewSummaryProps) {
  const { title, icon: OutcomeIcon, surfaceClass, accentClass } = REVIEW_OUTCOME_DISPLAY[status];

  return (
    <section
      aria-label="Review result"
      className={cn('flex gap-3 rounded-xl border p-4', surfaceClass)}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full bg-card shadow-xs',
          accentClass,
        )}
      >
        <OutcomeIcon className="size-5" aria-hidden />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <p className={cn('text-base font-semibold', accentClass)}>{title}</p>
          <p className="text-sm text-muted-foreground">
            {reviewedByName ? (
              <>
                By <span className="font-medium text-foreground">{reviewedByName}</span>
              </>
            ) : (
              'Reviewer not recorded'
            )}
            {reviewedAt && (
              <>
                <span className="max-sm:hidden"> · </span>
                <time dateTime={reviewedAt} className="whitespace-nowrap max-sm:block">
                  {formatDisplayDateTime(reviewedAt)}
                </time>
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Reviewer comment</span>
          <p className="rounded-lg bg-card px-3 py-2 text-sm leading-relaxed wrap-break-word whitespace-pre-line">
            {reviewerComment ?? <span className="text-muted-foreground">No comment was left.</span>}
          </p>
        </div>
      </div>
    </section>
  );
}
